'use client';

import { useEffect } from 'react';
import { logClientError } from '@/lib/errors/log-client';

// Root-level browser error listener. Mounted once from app/layout.tsx.
// Captures errors that fall through every React boundary:
//   - synchronous throws in event handlers
//   - unhandled promise rejections (failed fetches, awaited throws)
//   - chunk-load failures during code-splitting
// React render errors are already handled by app/error.tsx and global-error.tsx,
// so we tag those separately and don't double-log here.

export function ErrorReporter() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      // Filter noisy benign cross-origin errors that browsers report as
      // "Script error" with no stack and no useful context.
      if (!event.error && event.message === 'Script error.') return;
      logClientError({
        source: 'client',
        severity: 'error',
        message: event.message || (event.error && (event.error as Error).message) || 'Unknown window error',
        stack: (event.error as Error | undefined)?.stack ?? null,
        tags: ['window_onerror'],
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : (() => {
                try {
                  return JSON.stringify(reason);
                } catch {
                  return String(reason);
                }
              })();
      logClientError({
        source: 'client',
        severity: 'error',
        message: message || 'Unhandled promise rejection',
        stack: reason instanceof Error ? reason.stack ?? null : null,
        tags: ['unhandled_rejection'],
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return null;
}
